import React from 'react';
import { Control, FormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextInput from '../../form/TextInput';
import DateInput from '../../form/DateInput';
import SelectMenu from '../../form/SelectMenu';
import { InsuranceStrength } from '../../../data/models/insurance-strength.model';
import { Typography } from '@mui/material';
import CheckboxInput from '../../form/CheckboxInput';
import { InsuranceCoverageType } from '../../../services/insuranceUtils';
import validationUtils from '../../../services/validationUtils';
import { DateTime } from 'luxon';

interface InputItem {
  label: string;
  name: string;
  props: any;
  currency: boolean;
  enabledID?: number;
}

interface TextInputItemProps {
  item: InputItem;
  control: Control;
  formState: FormState<any>;
  required?: boolean;
  requiredFieldMessage: string;
  disabled?: () => boolean;
}

const TextInputItem = ({
  item,
  control,
  formState,
  required = true,
  requiredFieldMessage,
  disabled = () => false,
}: TextInputItemProps) => {
  const { t } = useTranslation('common');
  const rules: any = required ? { required: requiredFieldMessage } : {};
  if (item.currency) {
    rules.validate = validationUtils.rangeValidation(0, 100000000, t);
  }
  return (
    <TextInput
      label={item.label}
      name={item.name}
      type={item.currency ? 'number' : 'text'}
      inputProps={item.props}
      required={required}
      control={control}
      errors={formState.errors}
      rules={rules}
      disabled={disabled()}
    />
  );
};

interface InsuranceInfoFormProps {
  control: Control;
  formState: FormState<any>;
  watch: any;
  requiredFieldMessage: string;
  insuranceStrengths: InsuranceStrength[];
}

export default function InsuranceInfoForm({
  control,
  formState,
  requiredFieldMessage,
  insuranceStrengths,
  watch,
}: InsuranceInfoFormProps) {
  const { t } = useTranslation('common');

  const [minDate, setMinDate] = React.useState<DateTime>();
  const [maxDate, setMaxDate] = React.useState<DateTime>();
  React.useEffect(() => {
    setMinDate(DateTime.now().minus({ years: 5 }).startOf('year'));
    setMaxDate(DateTime.now().plus({ years: 15 }).endOf('year'));
  }, []);

  const commercialPolicyItems: InputItem[] = [
    {
      label: t('provider.insuranceCompany'),
      name: 'CommercialInsuranceCompanyName',
      props: { maxLength: 100 },
      currency: false,
    },
    {
      label: t('provider.insurancePolicyNum'),
      name: 'CommercialInsurancePolicyNumber',
      props: { maxLength: 50 },
      currency: false,
    },
  ];

  const autoPolicyItems: InputItem[] = [
    {
      label: t('provider.insuranceCompany'),
      name: 'AutoInsuranceCompanyName',
      props: { maxLength: 100 },
      currency: false,
    },
    {
      label: t('provider.insurancePolicyNum'),
      name: 'AutoInsurancePolicyNumber',
      props: { maxLength: 50 },
      currency: false,
    },
  ];

  const commmercialAggregateItems: InputItem[] = [
    {
      label: t('provider.insuranceEachOccurrence'),
      name: 'CommercialAggregateAmount',
      props: { maxLength: 12, step: 1000, min: 0 },
      currency: true,
    },
  ];

  const [autoLimitItems, setAutoLimitItems] = React.useState<InputItem[]>([]);

  const splitLimitItems: InputItem[] = [
    {
      label: t('provider.bodilyInjuryPerson'),
      name: 'AutoBodilyInjuryPerson',
      props: { maxLength: 12, step: 1000, min: 0 },
      currency: true,
      enabledID: InsuranceCoverageType.SplitLimits,
    },
    {
      label: t('provider.bodilyInjuryAccident'),
      name: 'AutoBodilyInjuryAccident',
      props: { maxLength: 12, step: 1000, min: 0 },
      currency: true,
      enabledID: InsuranceCoverageType.SplitLimits,
    },
    {
      label: t('provider.propertyDamage'),
      name: 'AutoPropertyDamage',
      props: { maxLength: 12, step: 1000, min: 0 },
      currency: true,
      enabledID: InsuranceCoverageType.SplitLimits,
    },
  ];

  const combinedLimitItems: InputItem[] = [
    {
      label: t('provider.insuranceCombinedLimit'),
      name: 'AutoCombinedLimit',
      props: { maxLength: 12, step: 1000, min: 0 },
      currency: true,
      enabledID: InsuranceCoverageType.CombinedSingleLimit,
    },
  ];

  const limitTypes = [
    {
      limitTypeID: InsuranceCoverageType.SplitLimits,
      limitTypeName: t('provider.insuranceSplit'),
    },
    {
      limitTypeID: InsuranceCoverageType.CombinedSingleLimit,
      limitTypeName: t('provider.insuranceCombined'),
    },
  ];

  const formCtrl = { control, formState, requiredFieldMessage };

  const coverageType = watch('AutoInsuranceCoverageTypeID');

  React.useEffect(() => {
    if (coverageType === 3) {
      setAutoLimitItems(splitLimitItems);
    }
    if (coverageType === 2) {
      setAutoLimitItems(combinedLimitItems);
    }
  }, [coverageType]);

  return (
    <>
      <Typography>{t('provider.commercial')}</Typography>
      {commercialPolicyItems.map((item) => (
        <TextInputItem key={item.name} item={item} {...formCtrl} />
      ))}
      <DateInput
        label={t('provider.insuranceExpires')}
        name="CommercialInsuranceExpiresOn"
        control={control}
        required={true}
        errors={formState.errors}
        minDate={minDate}
        maxDate={maxDate}
      />
      <SelectMenu
        label={t('provider.insuranceStrength')}
        name="CommercialInsuranceStrengthID"
        values={insuranceStrengths}
        valueAttr="insuranceStrengthID"
        labelAttr="insuranceStrengthName"
        required={true}
        control={control}
        errors={formState.errors}
        rules={{ required: requiredFieldMessage }}
      />
      {commmercialAggregateItems.map((item) => (
        <TextInputItem
          key={item.name}
          item={item}
          required={false}
          {...formCtrl}
        />
      ))}

      <Typography>{t('provider.auto')}</Typography>
      {autoPolicyItems.map((item) => (
        <TextInputItem key={item.name} item={item} {...formCtrl} />
      ))}
      <DateInput
        label={t('provider.insuranceExpires')}
        name="AutoInsuranceExpiresOn"
        control={control}
        required={true}
        errors={formState.errors}
        minDate={minDate}
        maxDate={maxDate}
      />
      <SelectMenu
        label={t('provider.insuranceStrength')}
        name="AutoInsuranceStrengthID"
        values={insuranceStrengths}
        valueAttr="insuranceStrengthID"
        labelAttr="insuranceStrengthName"
        required={true}
        control={control}
        errors={formState.errors}
        rules={{ required: requiredFieldMessage }}
      />
      <SelectMenu
        label={t('provider.insuranceCoverageType')}
        name="AutoInsuranceCoverageTypeID"
        values={limitTypes}
        valueAttr="limitTypeID"
        labelAttr="limitTypeName"
        required={true}
        control={control}
        errors={formState.errors}
        rules={{ required: requiredFieldMessage }}
      />
      {autoLimitItems.map((item) => (
        <TextInputItem
          key={item.name}
          item={item}
          {...formCtrl}
          required={false}
        />
      ))}
    </>
  );
}
